import { getCurrentAuthMethod, useAuth } from 'auth';
import { IdleLogoutGuard } from 'modules/app/components/molecules/idle-logout-guard/IdleLogoutGuard';
import { DevToolsMenu } from 'dev-tools';
import { useConfig } from 'providers/config/ConfigProvider';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import { QueryProvider } from 'providers/query-provider/QueryProvider';
import { PropsWithChildren } from 'react';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import ReactGA from 'react-ga4';
import { Outlet } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';
import { RouteBoundary } from 'components/organisms/route-boundary/RouteBoundary';
import { ToastHolder } from '@sanlam-glacier/app-common/modules/toasts';
import style from './holders.module.scss';

const GoogleAnalyticsProvider = ({ children }: PropsWithChildren) => {
  const trackingId = useConfig()?.googleAnalytics?.trackingId ?? '';
  const auth = useAuth();

  ReactGA.initialize(trackingId, {
    gaOptions: {
      userId: auth.getSignedInUser()?.username
    }
  });

  return children;
};

const ConfiguredQueryProvider = ({ children }: PropsWithChildren) => {
  const config = useConfig();
  const userContext = useUserContext();
  const { isUsingWebseal } = getCurrentAuthMethod(config);

  return (
    <QueryProvider
      bffApiUrl={replaceWithDynamicHost(config.apiUrl, config.apiHostMappings)}
      localJwtProvider={config.auth.gateway.jwtProvider}
      actingAsIntermediaryId={userContext.getActingAsIntermediaryUser()?.intermediaryCode}
      configureAccessToken={!isUsingWebseal}
    >
      {children}
    </QueryProvider>
  );
};

export const AuthenticatedLayout = () => {
  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <IdleLogoutGuard>
        <GoogleAnalyticsProvider>
          <ConfiguredQueryProvider>
            <RouteBoundary>
              <DevToolsMenu />
              <Outlet />
            </RouteBoundary>
            <ToastHolder className={style.customToastHolder} />
          </ConfiguredQueryProvider>
        </GoogleAnalyticsProvider>
      </IdleLogoutGuard>
    </QueryParamProvider>
  );
};

export default AuthenticatedLayout;
